var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import localize from "@/utils/localization";
import { ref, onMounted } from "vue";
import BarChart from "./Charts/BarChart.vue";
import { bytesLabelFormatter, bytesValueFormatter } from "./Charts/formatters";
import { fetchHistoryContentsSizeSummary, undeleteDatasetById, purgeDatasetById, } from "./service";
import RecoverableItemSizeTooltip from "./RecoverableItemSizeTooltip.vue";
import SelectedItemActions from "./SelectedItemActions.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import Heading from "@/components/Common/Heading.vue";
import { useRouter } from "vue-router/composables";
import { useToast } from "@/composables/toast";
import { useConfirmDialog } from "@/composables/confirmDialog";
import { useHistoryStore } from "@/stores/historyStore";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryStorageOverview',
    props: {
        historyId: {
            type: String,
            required: true,
        },
    },
    setup(__props) {
        const props = __props;
        const router = useRouter();
        const { success: successToast, error: errorToast } = useToast();
        const { confirm } = useConfirmDialog();
        const { getHistoryNameById } = useHistoryStore();
        const datasetsSizeSummaryMap = new Map();
        const topTenDatasetsBySizeData = ref(null);
        const activeVsDeletedTotalSizeData = ref(null);
        const isLoading = ref(true);
        const numberOfDatasetsToDisplayOptions = [10, 20, 50];
        const numberOfDatasetsToDisplay = ref(numberOfDatasetsToDisplayOptions[0]);
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            isLoading.value = true;
            const limit = Math.max(...numberOfDatasetsToDisplayOptions);
            const allDatasetsInHistorySizeSummary = yield fetchHistoryContentsSizeSummary(props.historyId, limit);
            allDatasetsInHistorySizeSummary.forEach((dataset) => datasetsSizeSummaryMap.set(dataset.id, dataset));
            buildGraphsData();
            isLoading.value = false;
        }));
        function buildGraphsData() {
            const allDatasetsInHistorySizeSummary = Array.from(datasetsSizeSummaryMap.values());
            topTenDatasetsBySizeData.value = buildTopTenDatasetsBySizeData(allDatasetsInHistorySizeSummary);
            activeVsDeletedTotalSizeData.value = buildActiveVsDeletedTotalSizeData(allDatasetsInHistorySizeSummary);
        }
        function buildTopTenDatasetsBySizeData(datasetsSizeSummary) {
            const topTenDatasetsBySize = datasetsSizeSummary
                .sort((a, b) => b.size - a.size)
                .slice(0, numberOfDatasetsToDisplay.value);
            return topTenDatasetsBySize.map((dataset) => ({
                id: dataset.id,
                label: dataset.name,
                value: dataset.size,
            }));
        }
        function buildActiveVsDeletedTotalSizeData(datasetsSizeSummary) {
            const activeDatasetsSize = datasetsSizeSummary
                .filter((dataset) => !dataset.deleted)
                .reduce((total, dataset) => total + dataset.size, 0);
            const deletedDatasetsSize = datasetsSizeSummary
                .filter((dataset) => dataset.deleted)
                .reduce((total, dataset) => total + dataset.size, 0);
            return [
                {
                    id: "active",
                    label: "Active",
                    value: activeDatasetsSize,
                },
                {
                    id: "deleted",
                    label: "Deleted",
                    value: deletedDatasetsSize,
                },
            ];
        }
        function isRecoverableDataPoint(dataPoint) {
            if (dataPoint) {
                const historiesSizeSummary = datasetsSizeSummaryMap.get(dataPoint.id || "");
                return (historiesSizeSummary === null || historiesSizeSummary === void 0 ? void 0 : historiesSizeSummary.deleted) || dataPoint.id === "deleted";
            }
            return false;
        }
        function onViewDataset(datasetId) {
            return __awaiter(this, void 0, void 0, function* () {
                router.push({
                    name: "DatasetDetails",
                    params: {
                        historyId: props.historyId,
                        datasetId: datasetId,
                    },
                });
            });
        }
        function onUndeleteDataset(datasetId) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const result = yield undeleteDatasetById(props.historyId, datasetId);
                    const dataset = datasetsSizeSummaryMap.get(datasetId);
                    if (dataset && !result.deleted) {
                        dataset.deleted = result.deleted;
                        datasetsSizeSummaryMap.set(datasetId, dataset);
                        successToast(localize("Dataset undeleted successfully."));
                        buildGraphsData();
                    }
                }
                catch (error) {
                    errorToast(`${error}`, localize("An error occurred while undeleting the dataset."));
                }
            });
        }
        function onPermanentlyDeleteDataset(datasetId) {
            return __awaiter(this, void 0, void 0, function* () {
                const confirmed = yield confirm(localize("Are you sure you want to permanently delete this dataset? This action cannot be undone."), {
                    title: localize("Permanently delete dataset?"),
                    okVariant: "danger",
                    okTitle: localize("Permanently delete"),
                    cancelTitle: localize("Cancel"),
                });
                if (!confirmed) {
                    return;
                }
                try {
                    const result = yield purgeDatasetById(props.historyId, datasetId);
                    const dataset = datasetsSizeSummaryMap.get(datasetId);
                    if (dataset && result) {
                        datasetsSizeSummaryMap.delete(datasetId);
                        successToast(localize("Dataset permanently deleted successfully."));
                        buildGraphsData();
                    }
                }
                catch (error) {
                    errorToast(`${error}`, localize("An error occurred while permanently deleting the dataset."));
                }
            });
        }
        return { __sfc: true, router, successToast, errorToast, confirm, getHistoryNameById, props, datasetsSizeSummaryMap, topTenDatasetsBySizeData, activeVsDeletedTotalSizeData, isLoading, numberOfDatasetsToDisplayOptions, numberOfDatasetsToDisplay, buildGraphsData, buildTopTenDatasetsBySizeData, buildActiveVsDeletedTotalSizeData, isRecoverableDataPoint, onViewDataset, onUndeleteDataset, onPermanentlyDeleteDataset, localize, BarChart, bytesLabelFormatter, bytesValueFormatter, RecoverableItemSizeTooltip, SelectedItemActions, LoadingSpan, Heading };
    }
});
